import {
  DEFAULT_APPEARANCE_OPTIONS,
  DEFAULT_DRESSING_OPTIONS,
  DEFAULT_PAGE_OPTIONS,
  DEFAULT_TABLE_OPTIONS,
} from './schemas/export.js';
import type {
  ExportAppearanceOptions,
  ExportDressingOptions,
  ExportPageOptions,
  ExportTableOptions,
} from './schemas/export.js';

/**
 * The four export option blocks (chantier 1) as one named, reusable preset —
 * export-rework chantier 2 (plan 2026-09-24, Task 1 / spec P2).
 *
 * Presets are full snapshots, not diffs against the defaults, so the
 * frontend, the future viewer and chantier 5's user presets all speak the
 * same shape without reconstructing it from a delta.
 */
export interface ExportPresetDefinition {
  id: 'lecture' | 'vue-densemble' | 'atelier' | 'dossier';
  /** French display name and one-line hint for the thumbnail card. */
  label: string;
  hint: string;
  page: ExportPageOptions;
  table: ExportTableOptions;
  dressing: ExportDressingOptions;
  appearance: ExportAppearanceOptions;
}

/** The four blocks a preset is matched against or applied onto. */
interface ExportPresetOptions {
  page: ExportPageOptions;
  table: ExportTableOptions;
  dressing: ExportDressingOptions;
  appearance: ExportAppearanceOptions;
}

/**
 * A fresh deep copy of the four DEFAULT_* blocks. Every preset starts from its
 * own copy so no definition shares a block — or a mutable container such as
 * `table.columnOrder` — with the exported defaults or with another preset.
 */
function defaultBlocks(): ExportPresetOptions {
  return structuredClone({
    page: DEFAULT_PAGE_OPTIONS,
    table: DEFAULT_TABLE_OPTIONS,
    dressing: DEFAULT_DRESSING_OPTIONS,
    appearance: DEFAULT_APPEARANCE_OPTIONS,
  });
}

const LECTURE = defaultBlocks();
const VUE_DENSEMBLE = defaultBlocks();
const ATELIER = defaultBlocks();
const DOSSIER = defaultBlocks();

export const EXPORT_PRESETS: readonly ExportPresetDefinition[] = [
  {
    id: 'lecture',
    label: 'Lecture',
    hint: '2-3 pages confortables',
    ...LECTURE,
  },
  {
    id: 'vue-densemble',
    label: "Vue d'ensemble",
    hint: 'tout sur une page',
    page: { ...VUE_DENSEMBLE.page, fit: 'une-seule-page' },
    table: { ...VUE_DENSEMBLE.table, density: 'compact' },
    dressing: VUE_DENSEMBLE.dressing,
    appearance: VUE_DENSEMBLE.appearance,
  },
  {
    id: 'atelier',
    label: 'Atelier',
    hint: 'affiche à assembler',
    page: {
      format: 'a3',
      orientation: 'landscape',
      margins: 'minimales',
      fontSizePt: 13,
      fit: 'aucun',
    },
    table: { ...ATELIER.table, repeatHeaders: false },
    dressing: {
      companyLogo: true,
      titleBlock: true,
      dates: false,
      footerText: null,
      pageNumbers: false,
      pmsSignature: 'anneau',
      assemblyMarks: true,
    },
    appearance: ATELIER.appearance,
  },
  {
    id: 'dossier',
    label: 'Dossier',
    hint: 'portrait, par dimension',
    page: { ...DOSSIER.page, orientation: 'portrait', fontSizePt: 11 },
    table: { ...DOSSIER.table, structure: 'transpose' },
    dressing: DOSSIER.dressing,
    appearance: DOSSIER.appearance,
  },
];

/**
 * Order-stable deep equality: object keys are sorted before comparison so
 * insertion order never matters, while array element order (meaningful for
 * `columnOrder` / `pageBreaksBefore`) is preserved as-is. Every nested field
 * is covered — nothing is special-cased or skipped.
 */
function stableStringify(value: unknown): string {
  if (value === null || typeof value !== 'object') {
    return JSON.stringify(value);
  }
  if (Array.isArray(value)) {
    return `[${value.map((item) => stableStringify(item)).join(',')}]`;
  }
  const entries = Object.entries(value as Record<string, unknown>).sort(([a], [b]) =>
    a.localeCompare(b),
  );
  const body = entries
    .map(([key, entryValue]) => `${JSON.stringify(key)}:${stableStringify(entryValue)}`)
    .join(',');
  return `{${body}}`;
}

function deepEqual(a: unknown, b: unknown): boolean {
  return stableStringify(a) === stableStringify(b);
}

/**
 * The table fields that describe *this matrix's* arrangement rather than a
 * way of presenting any matrix: per-palier widths, column order and forced
 * page breaks, all written by the viewer's « Aménager » tool (issue #11).
 * They are keyed by palier numbers and row keys, so no preset can
 * meaningfully carry them — they are neither part of a preset's identity nor
 * replaced when one is applied.
 */
type ExportTableArrangement = Pick<
  ExportTableOptions,
  'columnWidths' | 'columnOrder' | 'pageBreaksBefore'
>;

/** The table block minus its arrangement — the one place that lists those fields for matching. */
export function withoutArrangement(
  table: ExportTableOptions,
): Omit<ExportTableOptions, keyof ExportTableArrangement> {
  const { columnWidths: _widths, columnOrder: _order, pageBreaksBefore: _breaks, ...rest } = table;
  return rest;
}

/**
 * Whether two four-block snapshots are the same preset: every field
 * deep-equal except the table's arrangement. The rule behind both the
 * built-in matcher and the personal-preset one (`matchAnyExportPreset`).
 */
export function sameExportPresetOptions(a: ExportPresetOptions, b: ExportPresetOptions): boolean {
  return (
    deepEqual(a.page, b.page) &&
    deepEqual(withoutArrangement(a.table), withoutArrangement(b.table)) &&
    deepEqual(a.dressing, b.dressing) &&
    deepEqual(a.appearance, b.appearance)
  );
}

/**
 * The preset whose four blocks deep-equal the given state, else null
 * ("modifié"). The table's arrangement is ignored, so dragging a column
 * wider in « Aménager » does not flip the picker to « Personnalisé ».
 */
export function matchExportPreset(
  options: ExportPresetOptions,
): ExportPresetDefinition['id'] | null {
  return EXPORT_PRESETS.find((preset) => sameExportPresetOptions(options, preset))?.id ?? null;
}

/** Fresh, unaliased clones of the named preset's four blocks. */
export function applyExportPreset(id: ExportPresetDefinition['id']): ExportPresetOptions {
  const preset = EXPORT_PRESETS.find((candidate) => candidate.id === id);
  if (!preset) {
    throw new Error(`Unknown export preset id: ${id}`);
  }
  return structuredClone({
    page: preset.page,
    table: preset.table,
    dressing: preset.dressing,
    appearance: preset.appearance,
  });
}

/**
 * `applyExportPreset`, keeping the current table arrangement (widths, column
 * order, page breaks — cloned, so the result never aliases `currentTable`).
 * What the preset picker applies: choosing « Atelier » after arranging the
 * columns changes the presentation, not the matrix-specific layout.
 */
export function applyExportPresetKeepingArrangement(
  id: ExportPresetDefinition['id'],
  currentTable: ExportTableOptions,
): ExportPresetOptions {
  const applied = applyExportPreset(id);
  const arrangement: ExportTableArrangement = structuredClone({
    columnWidths: currentTable.columnWidths,
    columnOrder: currentTable.columnOrder,
    pageBreaksBefore: currentTable.pageBreaksBefore,
  });
  return { ...applied, table: { ...applied.table, ...arrangement } };
}
